import React, { useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { HelpOutline, AttachFile, FileUploadOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { backendClient } from "../../../../api/backend";
import SectionHeader from "../SectionHeader/SectionHeader";

const SupportTab = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const user = useSelector((state) => state.auth?.user?.user);

  const [supportObject, setSupportObject] = useState("");
  const [supportDescription, setSupportDescription] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const handleSupportSubmit = async () => {
    setIsSendingSupport(true);
    const newErrors = {};

    if (!supportObject) {
      newErrors.supportObject = t("error_support_object_required");
    }
    if (!supportDescription) {
      newErrors.supportDescription = t("error_support_description_required");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setIsSendingSupport(false);
      return;
    }

    const userEmail = user.email;

    try {
      await backendClient.sendSupport(
        supportObject,
        supportDescription,
        attachedFile,
        userEmail
      );

      enqueueSnackbar(t("support_request_submitted"), { variant: "success" });

      setSupportObject("");
      setSupportDescription("");
      setAttachedFile(null);
      setErrors({});
    } catch (error) {
      console.error("Error while submitting support request:", error);
      enqueueSnackbar(t("error_support_submission_failed"), { variant: "error" });
    }
    setIsSendingSupport(false);
  };

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    setAttachedFile(e.target.files[0]);
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 4,
        borderRadius: "16px",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<HelpOutline />}
          title={t("support_tab")}
          description={t("support_description")}
        />

        {/* Dropdown to Select Support Object */}
        <FormControl fullWidth sx={{ mt: 1, mb: errors.supportObject ? 0 : 3 }}>
          <InputLabel id="support-object-label">
            {t("support_object")}
          </InputLabel>
          <Select
            labelId="support-object-label"
            value={supportObject}
            onChange={(e) => setSupportObject(e.target.value)}
            label={t("support_object")}
            error={!!errors.supportObject}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: errors.supportObject
                  ? theme.palette.error.main
                  : theme.palette.divider,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: errors.supportObject
                  ? theme.palette.error.main
                  : theme.palette.primary.dark,
              },
              "& .MuiSelect-select": {
                color: theme.palette.text.secondary,
              },
            }}
          >
            <MenuItem value="Documents manquants">
              {t("missing_documents")}
            </MenuItem>
            <MenuItem value="Problème technique">
              {t("technical_issue")}
            </MenuItem>
            <MenuItem value="Conseil juridique">{t("legal_advice")}</MenuItem>
            <MenuItem value="Amélioration">{t("ameleration_support")}</MenuItem>
            <MenuItem value="Guide des procédures">
              {t("procedure_guidance")}
            </MenuItem>
            <MenuItem value="Retour d'information sur le système">
              {t("system_feedback")}
            </MenuItem>
            <MenuItem value="Autre">{t("other")}</MenuItem>
          </Select>
          {errors.supportObject && (
            <Typography
              color="error"
              variant="caption"
              sx={{ mt: 0.5, ml: 2 }}
            >
              {errors.supportObject}
            </Typography>
          )}
        </FormControl>

        {/* Multiline Text Area for Description */}
        <TextField
          label={t("support_description")}
          multiline
          rows={6}
          variant="outlined"
          fullWidth
          value={supportDescription}
          onChange={(e) => setSupportDescription(e.target.value)}
          error={!!errors.supportDescription}
          helperText={errors.supportDescription}
          sx={{ mb: 3 }}
          InputProps={{
            sx: {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: errors.supportDescription
                  ? theme.palette.error.main
                  : theme.palette.divider,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: errors.supportDescription
                  ? theme.palette.error.main
                  : theme.palette.primary.dark,
              },
            },
          }}
        />

        {/* Attached file information */}
        {attachedFile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              p: 2,
              borderRadius: "8px",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <FileUploadOutlined
              sx={{ color: theme.palette.primary.main, mr: 1.5 }}
            />
            <Typography
              variant="body2"
              sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
            >
              {t("selected_file")}:{" "}
              <span style={{ fontWeight: "normal" }}>{attachedFile.name}</span>
            </Typography>
          </Box>
        )}

        {/* Attach File and Send Buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexDirection: isSmall ? "column" : "row",
            "& > *": { width: isSmall ? "100%" : "auto" },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<AttachFile />}
            onClick={handleFileClick}
            disabled={isSendingSupport}
            sx={{
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              textTransform: "none",
              borderRadius: "8px",
              py: 1.25,
              px: 3,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            {t("attach_file")}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <Button
            variant="contained"
            disableElevation
            onClick={handleSupportSubmit}
            disabled={!supportObject || !supportDescription || isSendingSupport}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.mode === "dark" ? "#000" : "#fff",
              px: 4,
              py: 1.25,
              borderRadius: "8px",
              fontWeight: 600,
              ml: isSmall ? 0 : "auto",
              mt: isSmall ? 2 : 0,
              textTransform: "none",
              "&:hover": { bgcolor: theme.palette.primary.dark },
              "&.Mui-disabled": {
                bgcolor: alpha(theme.palette.primary.main, 0.4),
                color: theme.palette.mode === "dark" ? "#000" : "#fff",
              },
            }}
          >
            {isSendingSupport ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: "3px solid rgba(255,255,255,0.3)",
                    borderTopColor: "#fff",
                    animation: "spin 1s linear infinite",
                    marginRight: 8,
                  }}
                ></span>
                {t("sending")}
              </Box>
            ) : (
              t("send")
            )}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SupportTab;
